import { atom } from 'jotai';
import { atomWithStorage, atomFamily } from 'jotai/utils';
import { configureAtomDevtools } from '../utils/tracking';
import { createPersistedStorage } from '../utils/persistence';

export const CHAT_DRAFTS_STORAGE_KEY = 'la.chatDrafts';
const CHAT_DRAFTS_STORAGE_VERSION = 1;

const MAX_DRAFTS = 20;

interface DraftEntry {
  text: string;
  updatedAt: number;
}

type DraftMap = Record<string, DraftEntry>;

const chatDraftsAtom = configureAtomDevtools(
  atomWithStorage<DraftMap>(
    CHAT_DRAFTS_STORAGE_KEY,
    {},
    createPersistedStorage<DraftMap>({ version: CHAT_DRAFTS_STORAGE_VERSION }),
    { getOnInit: true },
  ),
  'chat/drafts',
);

function prune(map: DraftMap): DraftMap {
  const entries = Object.entries(map);
  if (entries.length <= MAX_DRAFTS) return map;
  entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt);
  return Object.fromEntries(entries.slice(0, MAX_DRAFTS));
}

export const chatDraftAtomFamily = atomFamily((sessionId: string) =>
  configureAtomDevtools(
    atom(
      (get) => get(chatDraftsAtom)[sessionId]?.text ?? '',
      (get, set, text: string) => {
        const current = get(chatDraftsAtom);
        if (text === '') {
          if (!(sessionId in current)) return;
          const next = { ...current };
          delete next[sessionId];
          set(chatDraftsAtom, next);
          return;
        }
        set(chatDraftsAtom, prune({ ...current, [sessionId]: { text, updatedAt: Date.now() } }));
      },
    ),
    `chat/draft/${sessionId}`,
  ),
);

/**
 * Drop EVERY cached draft.
 *
 * Drafts are unsent prompts keyed by session id, so they are account-scoped
 * content that must not survive a sign-out into the next account's shell. The
 * backing map is module-private, so the teardown registry
 * (`stores/auth/teardown.ts`) reaches it through this action rather than by
 * reaching into `chatDraftAtomFamily` one session at a time — the family has no
 * enumeration of which session ids it has ever seen.
 */
export const clearChatDraftsAction = configureAtomDevtools(
  atom<null, [], void>(null, (_get, set) => {
    set(chatDraftsAtom, {});
  }),
  'chat/clearDrafts',
);
